// ── UTFALLSMATRIS ─────────────────────────────────────────────────
// Budgetmatrisens rader, men med verkligt utfall och avvikelse. Svarar på
// den enda fråga budgeten finns för: når vi målet?
//
// Två källor med olika egenskaper:
//  · Qlik — per resultatenhet, uppdaterat var 15:e minut. Ger utfall per RAD
//    men bara för intäkter, varukostnad och lön.
//  · Bokföringen (SIE) — hela resultaträkningen, men bara per KONTOGRUPP och
//    en månad efter. Den är facit.
//
// Därför: raderna får Qlik-utfall där en resultatenhet finns, gruppraderna
// får bokföringen när månaden är stängd. Skiljer de sig syns det direkt,
// vilket i sig är nyttig information — det var så vi hittade att Qliks
// varukostnad periodiseras på försäljningsdagen och bokföringens på fakturan.
import React, { useMemo, useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getDailyUnits } from '../qlik';
import { RADER, GRUPP_LABELS, MANAD_LABELS, KOSTNADSGRUPPER, summering } from './budgetModel';
import { kontoGrupp } from '../sie';

const kr0 = (v) => Math.round(v / 1000);
const visa = (v) => (v ? kr0(v).toLocaleString('sv-SE') : '–');
const GRUPPER = ['intakt', 'vk', 'ovrigt', 'personal', 'avskr', 'finans'];

export default function UtfallsMatris({ bolag, ra, manader, rader }) {
  const [qlik, setQlik] = useState(null);
  const [bok, setBok] = useState(null);
  const [fel, setFel] = useState('');
  const [lage, setLage] = useState('utfall'); // utfall | avvikelse

  const raderDef = useMemo(() => RADER[bolag] || [], [bolag]);
  const nuYm = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [units, snap] = await Promise.all([
          getDailyUnits(),
          getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', bolag))),
        ]);
        if (!live) return;
        const agg = {}; // enhet → ym → { intakt, vk, lon }
        for (const u of units) {
          const ym = u.datum.slice(0, 4) + u.datum.slice(5, 7);
          const e = agg[u.enhet] || (agg[u.enhet] = {});
          const m = e[ym] || (e[ym] = { intakt: 0, vk: 0, lon: 0 });
          m.intakt += u.intakt || 0;
          m.vk += Math.max(Math.abs(u.kostnad || 0) - Math.abs(u.lon || 0), 0);
          m.lon += Math.abs(u.lon || 0);
        }
        setQlik(agg);
        const b = {}; // ym → grupp → kr
        snap.forEach((d) => {
          const v = d.data();
          if (!v.ym || !Array.isArray(v.rader)) return;
          const g = b[v.ym] || (b[v.ym] = {});
          for (const r of v.rader) {
            const gr = kontoGrupp(r.konto);
            if (!gr) continue;
            g[gr] = (g[gr] || 0) + (gr === 'intakt' ? -r.belopp : r.belopp);
          }
        });
        setBok(b);
      } catch (e) {
        if (live) setFel(e.message || 'Kunde inte läsa utfallet');
      }
    })();
    return () => { live = false; };
  }, [bolag]);

  // Utfall per rad ur Qlik. Personal läggs helt på Fast personal — Qlik vet
  // inget om fast/säsong, den uppdelningen finns bara i Ledning → Personal.
  const radUtfall = useMemo(() => {
    if (!qlik) return {};
    const ut = {};
    const enhSet = new Set(raderDef.filter((r) => r.qlikEnhet).map((r) => r.qlikEnhet));
    if (bolag === 'hotell') { enhSet.add('OH'); enhSet.add('Saknas'); enhSet.delete('Sportcenter'); }
    for (const r of raderDef) {
      if (!r.qlikEnhet) continue;
      const falt = r.grupp === 'intakt' ? 'intakt' : r.grupp === 'vk' ? 'vk' : null;
      if (!falt) continue;
      const src = qlik[r.qlikEnhet] || {};
      ut[r.id] = Object.fromEntries(manader.map((m) => [m, (src[m] || {})[falt] || 0]));
    }
    const lon = {};
    for (const [enhet, byYm] of Object.entries(qlik)) {
      if (!enhSet.has(enhet)) continue;
      for (const m of manader) lon[m] = (lon[m] || 0) + ((byYm[m] || {}).lon || 0);
    }
    ut['personal:fast'] = lon;
    return ut;
  }, [qlik, raderDef, manader, bolag]);

  // Gruppnivå: bokföringen är facit där månaden finns, annars Qlik-summan
  const gruppUtfall = useMemo(() => {
    const ut = {};
    for (const g of GRUPPER) {
      ut[g] = {};
      for (const m of manader) {
        const bokfort = bok && bok[m] ? bok[m][g] : undefined;
        if (bokfort !== undefined) { ut[g][m] = { kr: bokfort, kalla: 'bok' }; continue; }
        const ids = raderDef.filter((r) => r.grupp === g).map((r) => r.id);
        const summa = ids.reduce((s, id) => s + ((radUtfall[id] || {})[m] || 0), 0);
        if (summa) ut[g][m] = { kr: summa, kalla: 'qlik' };
      }
    }
    return ut;
  }, [bok, radUtfall, raderDef, manader]);

  const budget = (id, m) => (rader[id] || {})[m] || 0;
  // Visas i rutnätet: allt fram till och med innevarande månad.
  const harUtfall = (m) => m <= nuYm;
  // Räknas i summor och prognos: BARA avslutade månader. Innevarande månad
  // har bara några dagar på sig men en hel månads budget att mätas mot —
  // tas den med ser varje år ut som en katastrof den första i månaden.
  const avslutad = (m) => m < nuYm;

  // Avvikelse med rätt tecken: mer intäkt är bra, mer kostnad är dåligt
  const tecken = (grupp) => (KOSTNADSGRUPPER.has(grupp) ? -1 : 1);
  const farg = (grupp, diff) => {
    if (!diff) return 'var(--text-secondary)';
    return tecken(grupp) * diff > 0 ? '#1D6A5F' : '#A32D2D';
  };

  const th = { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '4px 3px', textAlign: 'right', fontFamily: 'system-ui,sans-serif' };
  const tdLabel = { fontSize: 11.5, padding: '2px 6px 2px 0', whiteSpace: 'nowrap', fontFamily: 'system-ui,sans-serif', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 1 };
  const td = { fontSize: 11, padding: '3px 4px', textAlign: 'right', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap' };
  const chip = (aktiv) => ({
    background: aktiv ? '#44365C' : 'transparent', color: aktiv ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });

  // Cell: utfall eller avvikelse, färgad mot budget
  const cell = (grupp, bud, utf, finns) => {
    if (!finns) return <td key={Math.random()} style={{ ...td, color: 'var(--border)' }}>·</td>;
    const diff = utf - bud;
    return (
      <td style={{ ...td, color: farg(grupp, diff), fontWeight: Math.abs(diff) > bud * 0.1 ? 700 : 400 }}
        title={`Budget ${visa(bud)} · Utfall ${visa(utf)} · Avvikelse ${visa(diff)} tkr`}>
        {lage === 'utfall' ? visa(utf) : (diff ? (diff > 0 ? '+' : '') + visa(diff) : '–')}
      </td>
    );
  };

  // Sammanfattning hittills: bara månader med utfall
  const hittills = (rad) => {
    const med = manader.filter((m) => avslutad(m) && rad.utf[m] !== undefined);
    const bud = med.reduce((s, m) => s + rad.bud(m), 0);
    const utf = med.reduce((s, m) => s + (rad.utf[m] || 0), 0);
    return { bud, utf, diff: utf - bud, antal: med.length };
  };

  if (fel) return <div className="card"><div style={{ fontSize: 13, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>{fel}</div></div>;
  if (!qlik) return <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Läser utfall…</div></div>;

  // Årets läge på översta nivån: rörelsens intäkter och driftsresultat
  const gruppRad = (g) => {
    const utf = Object.fromEntries(manader.map((m) => [m, gruppUtfall[g][m]?.kr]));
    const bud = (m) => raderDef.filter((r) => r.grupp === g).reduce((s, r) => s + (r.motpost ? -1 : 1) * budget(r.id, m), 0);
    return { utf, bud, sum: hittills({ utf, bud }) };
  };
  const intaktRad = gruppRad('intakt');

  // Resultat efter finansiella poster. Bara för månader med bokföring:
  // övriga kostnader, avskrivningar och räntor finns inte i Qlik, så en
  // Qlik-månad skulle se ut att gå med vinst bara för att kostnader saknas.
  const resultatRad = (() => {
    const sb = summering(rader || {}, raderDef, manader);
    const bud = (m) => sb[m]?.resultat || 0;
    const utf = {};
    for (const m of manader) {
      if (!bok || !bok[m]) continue;
      utf[m] = GRUPPER.reduce((acc, g) => acc + tecken(g) * (gruppUtfall[g][m]?.kr || 0), 0);
    }
    return { utf, bud, sum: hittills({ utf, bud }) };
  })();

  // Pace: skala HELA årsbudgeten med den takt vi håller hittills. Att räkna om
  // månadssnittet till tolv månader vore meningslöst här — mars är sextio
  // gånger juni. Budgetens egen månadsprofil bär säsongen, takten läggs ovanpå.
  const pace = (() => {
    const { bud, utf, antal } = intaktRad.sum;
    if (!antal || bud <= 0) return null;
    const arsbudget = manader.reduce((s, m) => s + intaktRad.bud(m), 0);
    const takt = utf / bud;
    const prognos = Math.round(arsbudget * takt);
    return { arsbudget, takt, prognos, diff: prognos - arsbudget };
  })();

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 4 }}>Målbild — intäkter hittills</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
              {intaktRad.sum.antal} {intaktRad.sum.antal === 1 ? 'avslutad månad' : 'avslutade månader'}
              {manader.includes(nuYm) && ' · innevarande månad pågår och räknas inte in'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', textAlign: 'right', fontFamily: 'system-ui,sans-serif' }}>
            {[['Budget', intaktRad.sum.bud, 'var(--text)'], ['Utfall', intaktRad.sum.utf, 'var(--text)'],
              ['Avvikelse', intaktRad.sum.diff, farg('intakt', intaktRad.sum.diff)]].map(([l, v, c]) => (
              <div key={l}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{l}</div>
                <div style={{ fontSize: 21, fontWeight: 700, color: c }}>
                  {l === 'Avvikelse' && v > 0 ? '+' : ''}{visa(v)}
                </div>
                {l === 'Avvikelse' && intaktRad.sum.bud > 0 && (
                  <div style={{ fontSize: 11, color: c }}>{(v / intaktRad.sum.bud * 100).toFixed(1).replace('.', ',')} %</div>
                )}
              </div>
            ))}
            {pace && (
              <div style={{ paddingLeft: 22, borderLeft: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Prognos helår</div>
                <div style={{ fontSize: 21, fontWeight: 700, color: farg('intakt', pace.diff) }}>{visa(pace.prognos)}</div>
                <div style={{ fontSize: 11, color: farg('intakt', pace.diff) }}>
                  {pace.diff > 0 ? '+' : ''}{visa(pace.diff)} mot {visa(pace.arsbudget)}
                </div>
              </div>
            )}
          </div>
        </div>
        {pace && (
          <div className="note">
            Prognosen skalar hela årsbudgeten med den takt du håller hittills ({(pace.takt * 100).toFixed(1).replace('.', ',')} %
            av budget). Den räknar alltså <strong>inte</strong> om månadssnittet till tolv månader — det hade blivit
            meningslöst i en säsongsverksamhet. Budgetens egen månadsprofil får bära säsongen, takten läggs ovanpå.
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Utfall mot budget — {ra.slice(0, 2)}/{ra.slice(2)} · tkr</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button onClick={() => setLage('utfall')} style={chip(lage === 'utfall')}>Utfall</button>
            <button onClick={() => setLage('avvikelse')} style={chip(lage === 'avvikelse')}>Avvikelse</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 940 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--card)', zIndex: 2 }} />
              {manader.map((m, i) => (
                <th key={m} style={{ ...th, opacity: harUtfall(m) ? 1 : 0.4, fontStyle: m === nuYm ? 'italic' : 'normal' }}
                  title={m === nuYm ? 'Pågår — räknas inte in i summor och prognos' : undefined}>
                  {MANAD_LABELS[i]}{m === nuYm ? '*' : ''}
                </th>
              ))}
              {['Budget', 'Utfall', 'Avvik.'].map((l) => <th key={l} style={{ ...th, borderLeft: '1px solid var(--border)' }}>{l}</th>)}
            </tr></thead>
            <tbody>
              {GRUPPER.map((g) => {
                const rDef = raderDef.filter((r) => r.grupp === g);
                if (!rDef.length) return null;
                const gr = gruppRad(g);
                return (
                  <React.Fragment key={g}>
                    <tr style={{ background: 'var(--surface-2)', fontWeight: 700 }}>
                      <td style={{ ...tdLabel, fontWeight: 700, background: 'linear-gradient(var(--surface-2), var(--surface-2)), var(--card)' }}>
                        {GRUPP_LABELS[g]}
                      </td>
                      {manader.map((m) => {
                        const u = gruppUtfall[g][m];
                        return (
                          <td key={m} style={{ ...td, fontWeight: 700, color: farg(g, (u?.kr || 0) - gr.bud(m)) }}
                            title={u ? `${u.kalla === 'bok' ? 'Bokföringen (facit)' : 'Qlik'} · budget ${visa(gr.bud(m))}` : 'Inget utfall än'}>
                            {u ? (lage === 'utfall' ? visa(u.kr) : visa(u.kr - gr.bud(m))) : '·'}
                            {u?.kalla === 'bok' && <span style={{ fontSize: 8, verticalAlign: 'super', opacity: 0.6 }}>B</span>}
                          </td>
                        );
                      })}
                      <td style={{ ...td, fontWeight: 700, borderLeft: '1px solid var(--border)' }}>{visa(gr.sum.bud)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{visa(gr.sum.utf)}</td>
                      <td style={{ ...td, fontWeight: 700, color: farg(g, gr.sum.diff) }}>
                        {gr.sum.diff > 0 ? '+' : ''}{visa(gr.sum.diff)}
                      </td>
                    </tr>
                    {rDef.map((r) => {
                      const utf = radUtfall[r.id];
                      const s = utf ? hittills({ utf, bud: (m) => budget(r.id, m) }) : null;
                      return (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={tdLabel}>{r.label}</td>
                          {manader.map((m) => (
                            <React.Fragment key={m}>
                              {cell(g, budget(r.id, m), (utf || {})[m] || 0, utf && harUtfall(m))}
                            </React.Fragment>
                          ))}
                          <td style={{ ...td, color: 'var(--text-secondary)', borderLeft: '1px solid var(--border)' }}>{s ? visa(s.bud) : '–'}</td>
                          <td style={{ ...td, color: 'var(--text-secondary)' }}>{s ? visa(s.utf) : '–'}</td>
                          <td style={{ ...td, fontWeight: 600, color: s ? farg(g, s.diff) : 'var(--text-secondary)' }}>
                            {s ? `${s.diff > 0 ? '+' : ''}${visa(s.diff)}` : '–'}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop: '2px solid var(--text-secondary)', fontWeight: 700 }}>
                <td style={{ ...tdLabel, fontWeight: 700, paddingTop: 6 }}>Resultat</td>
                {manader.map((m) => {
                  const u = resultatRad.utf[m];
                  const b = resultatRad.bud(m);
                  return (
                    <td key={m} style={{ ...td, fontWeight: 700, paddingTop: 6, color: u === undefined ? 'var(--border)' : farg('intakt', u - b) }}
                      title={u === undefined ? 'Resultat kräver bokföring för månaden' : `Budget ${visa(b)} · Utfall ${visa(u)} · Avvikelse ${visa(u - b)} tkr`}>
                      {u === undefined ? '·' : (lage === 'utfall' ? visa(u) : visa(u - b))}
                    </td>
                  );
                })}
                <td style={{ ...td, fontWeight: 700, paddingTop: 6, borderLeft: '1px solid var(--border)' }}>{visa(resultatRad.sum.bud)}</td>
                <td style={{ ...td, fontWeight: 700, paddingTop: 6 }}>{visa(resultatRad.sum.utf)}</td>
                <td style={{ ...td, fontWeight: 700, paddingTop: 6, color: farg('intakt', resultatRad.sum.diff) }}>
                  {resultatRad.sum.diff > 0 ? '+' : ''}{visa(resultatRad.sum.diff)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="note">
          Alla belopp i tkr. Grön betyder bättre än budget, röd sämre — för kostnader är mindre bättre.
          Fetstil markerar avvikelser över 10 %. Punkt betyder att månaden inte passerat eller saknar källa.
          <strong>Månaden märkt med * pågår</strong> — den visas i rutnätet men räknas varken in i summakolumnerna
          eller prognosen, eftersom några dagars försäljning annars ställs mot en hel månads budget.
          <strong> Raderna kommer från Qlik</strong> (uppdateras var 15:e minut) och finns bara där en resultatenhet
          är kopplad. <strong>Gruppraderna märkta <sup>B</sup> kommer från bokföringen</strong> och är facit —
          de täcker allt, även övriga kostnader, avskrivningar och räntor, men bara till och med senast uppladdade
          SIE-fil. Skiljer sig raden från gruppen är det oftast periodisering: Qlik tar varukostnaden på
          försäljningsdagen, bokföringen på fakturadatum.
          <strong> Resultat</strong> är intäkter minus samtliga kostnadsgrupper (efter finansiella poster) och
          visas bara för månader med bokföring — Qlik saknar övriga kostnader, avskrivningar och räntor.
        </div>
      </div>
    </>
  );
}
